import { useState, useEffect, useRef } from 'react';
import axios from 'axios'; // Import axios
import { useLocation } from 'react-router-dom';
import Header from '../components/Header';
import { Card, CardHeader, CardContent, CardTitle } from '@/components/common/Card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from "@/components/ui/dialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@radix-ui/react-collapsible";
import { ChevronDown, ChevronUp, Loader2 } from "lucide-react";
import { jsPDF } from "jspdf";

// Define the NewsArticle interface (matching App.tsx)
interface NewsArticle {
  title: string;
  link: string;
  snippet: string;
  source: string;
  date: string;
  imageUrl?: string;
  content?: string;
  analysis?: {
    analysis: string;
    timestamp: string;
  };
}

// Define Project interface matching Dossier.tsx / Leads.tsx
interface Project {
  id: string;
  title: string;
  authority: string; // Mapped from source
  publication_date: string;
  country: string; // Will be set to 'N/A' for now
  description: string; // Mapped from snippet
  ted_link: string; // Mapped from link
  status: 'new' | 'inProgress' | 'contacted' | 'qualified' | 'closed'; // LeadStatus type
}

export function AnalysisPage() {
  const [analyzedNews, setAnalyzedNews] = useState<NewsArticle[]>([]);
  const [selectedArticles, setSelectedArticles] = useState<{ [key: string]: boolean }>({});
  const [createdLeads, setCreatedLeads] = useState<Set<string>>(new Set()); // Re-add state for existing leads
  const [isCreatingDossier, setIsCreatingDossier] = useState(false);
  const [dossierDialogOpen, setDossierDialogOpen] = useState(false);
  const [createdDossierContent, setCreatedDossierContent] = useState<string>('');
  const location = useLocation();
  const highlightedCardRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // Load analyzed articles from localStorage
    const loadAnalyzedNews = () => {
      const savedAnalyzed = localStorage.getItem('analyzedNews');
      if (savedAnalyzed) {
        try {
          setAnalyzedNews(JSON.parse(savedAnalyzed));
        } catch (e) {
          console.error("Failed to parse analyzed news from localStorage", e);
          localStorage.removeItem('analyzedNews');
          setAnalyzedNews([]);
        }
      } else {
        setAnalyzedNews([]);
      }
    };

    // Load existing leads from localStorage
    const loadExistingLeads = () => {
      const savedLeadsString = localStorage.getItem('projectLeads');
      if (savedLeadsString) {
        try {
          const savedLeads: Project[] = JSON.parse(savedLeadsString);
          setCreatedLeads(new Set(savedLeads.map(lead => lead.id)));
        } catch (e) {
          console.error("Failed to parse leads from localStorage", e);
          localStorage.removeItem('projectLeads'); // Clear corrupted data
        }
      }
    };

    loadAnalyzedNews();
    loadExistingLeads(); // Load leads on mount

    // Listen for storage changes to update the list dynamically
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'analyzedNews') {
        loadAnalyzedNews();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Cleanup listener
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Effect to handle scrolling and highlighting based on hash
  useEffect(() => {
    if (location.hash) {
      const id = decodeURIComponent(location.hash.substring(1));
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.classList.add('highlight');
        const timer = setTimeout(() => {
          element.classList.remove('highlight');
        }, 2000);
        return () => clearTimeout(timer);
      }
    }
  }, [location.hash, analyzedNews]);

  const removeAnalysis = (link: string) => {
     const updatedNews = analyzedNews.filter(article => article.link !== link);
     setAnalyzedNews(updatedNews);
     try {
        localStorage.setItem('analyzedNews', JSON.stringify(updatedNews));
        toast.success("Analyse erfolgreich entfernt.");
         // Dispatch storage event so other components update
        window.dispatchEvent(new StorageEvent('storage', { key: 'analyzedNews' }));
     } catch (e) {
        console.error("Failed to save updated analyzed news to localStorage", e);
        toast.error("Fehler beim Entfernen der Analyse.");
        // Revert state if save fails?
        setAnalyzedNews(analyzedNews); 
     }
  };

  // Function to handle article selection
  const handleToggleArticle = (articleLink: string) => {
    setSelectedArticles(prev => ({
      ...prev,
      [articleLink]: !prev[articleLink]
    }));
  };

  // Function to create a lead from an article (re-added)
  const handleCreateLead = (article: NewsArticle) => {
    const newLead: Project = {
      id: article.link, // Use link as ID
      title: article.title,
      authority: article.source, // Map source to authority
      publication_date: article.date,
      country: 'N/A', // Set country to N/A for now
      description: article.analysis?.analysis ?? article.snippet, // Use analysis if available, else snippet
      ted_link: article.link, // Use link for ted_link
      status: 'new',
    };

    try {
      const savedLeadsString = localStorage.getItem('projectLeads');
      const currentLeads: Project[] = savedLeadsString ? JSON.parse(savedLeadsString) : [];
      
      if (currentLeads.some(lead => lead.id === newLead.id)) {
        toast.info('Dieser Artikel ist bereits ein Lead.');
        return;
      }

      const updatedLeads = [...currentLeads, newLead];
      localStorage.setItem('projectLeads', JSON.stringify(updatedLeads));
      setCreatedLeads(prev => new Set(prev).add(newLead.id)); // Update state to disable button
      toast.success('Lead erfolgreich erstellt!');
      window.dispatchEvent(new StorageEvent('storage', { key: 'projectLeads' }));
    } catch (e) {
      console.error("Failed to save lead to localStorage", e);
      toast.error('Fehler beim Speichern des Leads.');
    }
  };

  // Function to create the dossier
  const handleCreateDossier = async () => {
    const selectedArticlesList = analyzedNews.filter(article => selectedArticles[article.link]);
    
    if (selectedArticlesList.length === 0) {
      toast.warning('Bitte wählen Sie mindestens einen Artikel aus.');
      return;
    }

    setIsCreatingDossier(true);
    toast.info(`Erstelle Dossier mit ${selectedArticlesList.length} Artikel(n)...`);

    try {
      // Format articles for the API
      const formattedArticles = selectedArticlesList.map(article => ({
        title: article.title,
        link: article.link,
        source: article.source,
        date: article.date,
        snippet: article.snippet,
        analysis: typeof article.analysis === 'object' ? article.analysis.analysis : article.analysis
      }));

      const response = await axios.post('/api/dossier', // Use relative API path
        {
          articles: formattedArticles
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          timeout: 90000 // Increased timeout for potentially long operation
        }
      );

      console.log("API Response Data:", response.data); // Log the response data

      const dossierData = response.data.data?.dossier || 
                         response.data.dossier || 
                         response.data; // Adjust based on actual API response structure

      if (dossierData && typeof dossierData === 'string') { // Assuming dossier content is a string
        setCreatedDossierContent(dossierData);
        setDossierDialogOpen(true); // Open the dialog to show the dossier
        toast.success('Dossier erfolgreich erstellt!');
        setSelectedArticles({}); // Deselect articles after successful creation
      } else {
        console.error("Dossier creation response format unknown or empty:", response.data);
        toast.error('Fehler beim Erstellen des Dossiers: Keine validen Daten erhalten.');
      }
    } catch (err) {
      console.error('Dossier creation error:', err);
      let errorMessage = 'Ein unerwarteter Fehler ist aufgetreten.';
      if (axios.isAxiosError(err)) {
        if (err.code === 'ECONNABORTED') {
            errorMessage = 'Die Dossier-Erstellung dauert zu lange. Bitte versuchen Sie es später erneut.';
        } else {
            errorMessage = `Fehler: ${err.response?.data?.detail || err.response?.data?.message || err.message}`;
        }
        console.error('Full error response:', err.response?.data);
      }
      toast.error(errorMessage);
    } finally {
      setIsCreatingDossier(false);
    }
  };

  const downloadDossier = () => {
    if (!createdDossierContent) return;
    
    try {
      // Create PDF document
      const doc = new jsPDF();
      
      // Set German font - needs to support German umlauts
      doc.setFont("helvetica");
      
      // Set title
      doc.setFontSize(16);
      doc.text("Projektdossier", 20, 20);
      
      // Add date
      doc.setFontSize(10);
      doc.text(`Erstellt am: ${new Date().toLocaleDateString('de-DE')}`, 20, 30);
      
      // Set content font
      doc.setFontSize(12);
      
      // Format the text for PDF (need to split into lines)
      const textLines = doc.splitTextToSize(createdDossierContent, 170); // 170 is width in mm
      
      // Add the content text
      doc.text(textLines, 20, 40);
      
      // Save the PDF with German filename format
      doc.save(`Projektdossier_${new Date().toLocaleDateString('de-DE').replace(/\./g, '-')}.pdf`);
      
      // Close dialog after download
      setDossierDialogOpen(false);
      toast.success("Dossier als PDF heruntergeladen");
    } catch (error) {
      console.error("Error creating PDF:", error);
      toast.error("Fehler beim Erstellen der PDF-Datei");
      
      // Fallback to plain text download if PDF creation fails
      try {
        const blob = new Blob([createdDossierContent], { type: 'text/plain' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Projektdossier_${new Date().toLocaleDateString('de-DE').replace(/\./g, '-')}.txt`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.info("Dossier als Text-Datei heruntergeladen");
      } catch (fallbackError) {
        console.error("Fallback download failed:", fallbackError);
        toast.error("Download fehlgeschlagen");
      }
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4">
        <Header />
        <main className="py-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-3xl font-bold tracking-tight">Gespeicherte Analysen</h2>
            <Button
              onClick={handleCreateDossier}
              disabled={isCreatingDossier || Object.values(selectedArticles).filter(Boolean).length === 0}
            >
              {isCreatingDossier ? 'Erstelle Dossier...' : `Dossier erstellen (${Object.values(selectedArticles).filter(Boolean).length})`}
            </Button>
          </div>

          {analyzedNews.length === 0 ? (
            <div className="text-center text-muted-foreground py-16">
              <p className="text-lg mb-2">Keine Analysen gespeichert.</p>
              <p>Analysieren Sie Artikel in den Suchergebnissen, um sie hier anzuzeigen.</p>
            </div>
          ) : (
            <div className="flex flex-col gap-6">
              {analyzedNews.map((article) => (
                <div key={article.link} className="relative">
                  <input
                    type="checkbox"
                    checked={selectedArticles[article.link] || false}
                    onChange={() => handleToggleArticle(article.link)}
                    className="absolute top-4 left-4 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer z-10"
                    aria-label={`Select article ${article.title} for dossier`}
                  />
                  <Card
                    id={article.link}
                    className={cn(
                      "flex flex-col transition-all duration-300",
                      selectedArticles[article.link] && "ring-2 ring-primary ring-offset-2"
                    )}
                    ref={highlightedCardRef}
                  >
                    <CardHeader className="pl-12">
                      <CardTitle className="text-lg font-semibold line-clamp-2">{article.title}</CardTitle>
                      <div className="text-sm text-muted-foreground flex items-center justify-between mt-1">
                        <span>{article.source}</span>
                        <span>{new Date(article.date).toLocaleDateString('de-DE')}</span>
                      </div>
                    </CardHeader>
                    <CardContent className="flex-1 space-y-3">
                      {article.analysis ? (
                        <Collapsible 
                          className="bg-muted p-3 rounded-md text-sm"
                          open={selectedArticles[article.link]}
                          onOpenChange={(isOpen: boolean) => {
                            setSelectedArticles(prev => ({
                              ...prev,
                              [article.link]: isOpen
                            }));
                          }}
                        >
                          <div className="flex items-center justify-between">
                            <h4 className="font-semibold text-primary">Projektanalyse:</h4>
                            <CollapsibleTrigger className="hover:bg-muted-foreground/10 p-1 rounded">
                              <div className="flex items-center gap-1">
                                <span className="text-xs">Details</span>
                                {selectedArticles[article.link] ? 
                                  <ChevronUp className="h-4 w-4" /> : 
                                  <ChevronDown className="h-4 w-4" />
                                }
                              </div>
                            </CollapsibleTrigger>
                          </div>
                          <CollapsibleContent className="mt-2">
                            <p className="whitespace-pre-wrap break-words">{article.analysis.analysis}</p>
                            <p className="text-xs text-muted-foreground mt-2">
                              Analysiert am: {new Date(article.analysis.timestamp).toLocaleString('de-DE')}
                            </p>
                          </CollapsibleContent>
                        </Collapsible>
                      ) : (
                         <p className="text-muted-foreground italic">Keine Analyse verfügbar.</p>
                      )}
                      <div className="flex items-center justify-between text-sm">
                           <a
                              href={article.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary hover:underline"
                          >
                              Originalartikel
                          </a>
                           <div className="flex items-center space-x-2">
                              <Button 
                                 variant="outline" 
                                 size="sm" 
                                 className="h-auto p-1 text-xs"
                                 onClick={() => handleCreateLead(article)}
                                 disabled={createdLeads.has(article.link)}
                                 aria-label={`Create lead for ${article.title}`}
                              >
                                 {createdLeads.has(article.link) ? 'Lead erstellt' : 'Lead erstellen'}
                              </Button>
                              <Button 
                                 variant="ghost" 
                                 size="sm" 
                                 className="text-destructive hover:bg-destructive/10 hover:text-destructive h-auto p-1"
                                 onClick={() => removeAnalysis(article.link)}
                                 aria-label={`Remove analysis for ${article.title}`}
                              >
                                  ✕ Entfernen
                              </Button>
                           </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              ))}
            </div>
          )}
        </main>
      </div>

      {/* Dossier Result Dialog */}
      <Dialog open={dossierDialogOpen} onOpenChange={setDossierDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[80vh] flex flex-col">
          <DialogHeader className="flex-none">
            <DialogTitle>Generiertes Dossier</DialogTitle>
            <DialogDescription>
              Das Dossier wurde aus den ausgewählten Analysen erstellt.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-hidden flex flex-col">
            <div className="mt-4 bg-muted rounded-lg p-4 overflow-y-auto" style={{ maxHeight: '300px' }}>
              <div className="prose max-w-none dark:prose-invert whitespace-pre-wrap">
                {createdDossierContent || "Fehler beim Laden des Dossier-Inhalts."}
              </div>
            </div>
          </div>
          <DialogFooter className="flex-none mt-4">
            <Button variant="outline" onClick={() => setDossierDialogOpen(false)}>Schließen</Button>
            <Button onClick={downloadDossier} disabled={!createdDossierContent}>Herunterladen (PDF)</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Creating Dossier Notification */}
      {isCreatingDossier && (
        <div className="fixed bottom-4 left-4 bg-background border shadow-md rounded-lg p-3 max-w-[300px] z-50 flex items-center gap-2 overflow-hidden">
          <Loader2 className="h-4 w-4 animate-spin text-primary flex-shrink-0" />
          <div className="text-sm overflow-hidden">
            <div className="font-medium">Erstelle Dossier</div>
            <div className="text-muted-foreground text-xs truncate w-full">
              {`${Object.values(selectedArticles).filter(Boolean).length} Artikel werden verarbeitet...`}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AnalysisPage; 